import { useMemo, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { useCmsVersion } from '../cms/store'
import MediaImage from '../components/MediaImage'
import NewsletterBlock from '../components/NewsletterBlock'
import ProductCard from '../components/ProductCard'
import SectionHead from '../components/SectionHead'
import StoryCard from '../components/StoryCard'
import CategorySpotlight from '../components/CategorySpotlight'
import { revealDelay } from '../lib/reveal'
import { closetBanner, homeCategory, homeFeed, products, resolveHomeBoot } from '../data/content'
import { getBoot } from '../seo/boot'
import { publicPath, storyPath } from '../lib/paths'

function Arrow() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  )
}

export default function Home() {
  const cmsVersion = useCmsVersion()
  const { hero, hottest, categorySpotlights, more } = useMemo(() => {
    void cmsVersion
    const boot = getBoot() ?? (typeof window !== 'undefined' ? window.__TIGG_BOOT__ : undefined)
    if (boot?.home) {
      const resolved = resolveHomeBoot(boot.home)
      if (resolved) return resolved
    }
    return homeFeed()
  }, [cmsVersion])

  if (!hero) return null

  const titleWords = hero.title.split(/\s+/)
  const afterTitle = 0.48 + titleWords.length * 0.055

  return (
    <main>
      <section className="hero-intro grid min-w-0 grid-cols-1 gap-[clamp(20px,3.5vw,56px)] py-[clamp(28px,4.5vw,64px)] lg:h-[calc(100svh-7.25rem)] lg:min-h-[36rem] lg:grid-cols-2 lg:items-stretch lg:gap-x-[clamp(32px,4vw,72px)] lg:py-6">
        <Link
          to={storyPath(hero.slug)}
          aria-label={hero.title}
          className="hero-photo relative block min-h-[280px] overflow-hidden bg-sand aspect-[4/5] lg:h-full lg:min-h-0 lg:aspect-auto"
        >
          <MediaImage
            src={hero.image}
            size="hero"
            priority
            alt={hero.imageAlt || hero.title}
            className="block h-full w-full object-cover object-top"
          />
        </Link>
        <div className="flex min-w-0 flex-col justify-center gap-5 lg:max-w-[38rem]">
          <div className="flex flex-col gap-3">
            <div
              className="hero-rise text-caps flex items-center gap-3.5"
              style={{ '--hero-delay': '0.28s' } as CSSProperties}
            >
              <span className="text-rose">{homeCategory(hero)}</span>
              <span className="text-muted">{hero.date}</span>
            </div>
            <span
              className="hero-rule"
              style={{ '--hero-delay': '0.4s' } as CSSProperties}
              aria-hidden="true"
            />
          </div>
          <h1 className="text-caps-hero m-0 text-balance text-ink">
            <Link to={storyPath(hero.slug)} className="hover:text-rose">
              {titleWords.map((word, index) => (
                <span key={`${word}-${index}`}>
                  <span className="hero-title-mask">
                    <span
                      className="hero-title-word"
                      style={{ '--hero-delay': `${0.48 + index * 0.055}s` } as CSSProperties}
                    >
                      {word}
                    </span>
                  </span>
                  {index < titleWords.length - 1 ? ' ' : null}
                </span>
              ))}
            </Link>
          </h1>
          <p
            className="hero-rise m-0 leading-relaxed text-copy"
            style={{ '--hero-delay': `${afterTitle + 0.08}s` } as CSSProperties}
          >
            {hero.dek}
          </p>
          <Link
            to={storyPath(hero.slug)}
            className="hero-rise text-caps inline-flex items-center gap-2 self-start border-b border-ink pb-[5px] hover:border-rose hover:text-rose"
            style={{ '--hero-delay': `${afterTitle + 0.2}s` } as CSSProperties}
          >
            Read the story
            <Arrow />
          </Link>
        </div>
      </section>

      <section className="py-[clamp(28px,4.5vw,60px)]">
        <SectionHead title="Hottest stuff" aside="Just in" />
        <div className="grid grid-cols-1 gap-x-5 gap-y-8 min-[480px]:grid-cols-2 md:grid-cols-4">
          {hottest.map((story, index) => (
            <StoryCard key={story.slug} story={story} stagger={index} compact />
          ))}
        </div>
      </section>

      {categorySpotlights.length > 0 && (
        <section className="py-[clamp(28px,4.5vw,60px)]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-x-10">
            {categorySpotlights.map((spotlight, index) => (
              <div
                key={spotlight.name}
                data-reveal
                style={revealDelay(index * 0.1)}
                className={
                  index > 0
                    ? 'relative min-w-0 lg:before:absolute lg:before:inset-y-0 lg:before:-left-5 lg:before:w-px lg:before:bg-rule'
                    : 'min-w-0'
                }
              >
                <CategorySpotlight
                  name={spotlight.name}
                  href={spotlight.href}
                  imageStory={spotlight.image}
                  leadStory={spotlight.lead}
                  listStories={spotlight.list}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="py-[clamp(28px,4.5vw,60px)]">
        <SectionHead title="Shop my closet" aside="Enter" href={publicPath('/closet')} />
        <div data-reveal>
          <Link to={publicPath('/closet')} aria-label="Shop my closet" className="block overflow-hidden bg-sand">
            <MediaImage
              src={closetBanner}
              size="hero"
              sizes="100vw"
              alt="Shop my closet"
              className="aspect-[19/5] w-full object-cover object-center"
            />
          </Link>
          <p className="mt-5 max-w-[46ch] leading-relaxed text-copy">
            pieces i've actually worn, now looking for a second life. new drops every other friday.
          </p>
        </div>
      </section>

      <section className="py-[clamp(28px,4.5vw,60px)]">
        <SectionHead title="More to read" aside="All stories" href={publicPath('/stories')} />
        <div className="grid grid-cols-1 gap-x-5 gap-y-8 min-[480px]:grid-cols-2 md:grid-cols-4">
          {more.map((story, index) => (
            <StoryCard key={story.slug} story={story} stagger={index} compact />
          ))}
        </div>
      </section>

      <section id="picks" className="py-[clamp(28px,4.5vw,60px)]">
        <SectionHead title="Sky's latest favs" aside="TIGG Picks" bordered={false} />
        <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {products.slice(0, 10).map((product, index) => (
            <ProductCard key={product.slug} product={product} compact stagger={index} />
          ))}
        </div>
      </section>

      <NewsletterBlock />
    </main>
  )
}
